import { emptyValue } from './constants';
import { normalizeText } from './filters';
import {
  formatCapacidade,
  formatCargaNumero,
  formatCargasResumo,
  formatCidadeEstado,
  formatDateTimeResumo,
  formatDistanceKm,
  formatDisponibilidadeTms,
  formatEnderecoLinha,
  formatPopupDateTime,
  formatStatusViagensResumo,
  formatTiposOperacaoResumo,
  formatViagemDateResumo,
  formatViagemValuesResumo,
  formatViagensResumo,
  getPlotagemLabel,
  getStatusPatioLabel,
  getStatusPatioVariant,
  getValueOrFallback
} from './formatters';

export const getCategoriaPorDescricao = descricaoValor => {
  const descricao = String(descricaoValor || '').toUpperCase();
  if (descricao.includes('CAVALO')) return 'cavalo';
  if (descricao.includes('TOCO')) return 'toco';
  if (descricao.includes('TRUCK')) return 'truck';
  if (descricao.includes('CARRETA') || descricao.includes('REBOQUE') || descricao.includes('BITREM') || descricao.includes('RODOTREM')) return 'carreta';

  return 'outros';
};

export const getCategoriaSincronizavelPorDescricao = descricaoValor => {
  const categoria = getCategoriaPorDescricao(descricaoValor);
  return categoria === 'cavalo' ? 'outros' : categoria;
};

export const getCategoriaVeiculo = veiculo => {
  if (Number(veiculo?.tipoVeiculo) === 1) return 'carreta';
  return getCategoriaPorDescricao(veiculo?.modeloVeicular?.descricao);
};

export const getTipoModeloExibicao = veiculo => {
  if (veiculo?.tipoVeiculo === 1) {
    return getValueOrFallback(veiculo?.modeloVeicular?.descricao);
  }

  return getValueOrFallback(veiculo?.segmentoVeiculo?.descricao || veiculo?.modeloVeicular?.descricao);
};

const getStableRowId = (values, fallback) => {
  const identifier = values.find(value => (
    value !== null
    && value !== undefined
    && String(value).trim() !== ''
  ));

  return identifier ?? fallback;
};

export const getMotoristaRowId = (motorista, index = 0) => getStableRowId(
  [
    motorista?.codigo,
    motorista?.protocolo,
    motorista?.cpf,
    motorista?.id === emptyValue ? null : motorista?.id
  ],
  `motorista-${index}`
);

export const getVeiculoRowId = (veiculo, index = 0) => getStableRowId(
  [
    veiculo?.codigo,
    veiculo?.protocolo,
    veiculo?.numeroFrota,
    veiculo?.placa,
    veiculo?.id === emptyValue ? null : veiculo?.id
  ],
  `veiculo-${index}`
);

export const mapMotorista = (motorista, index = 0) => ({
  id: getMotoristaRowId(motorista, index),
  codigo: motorista?.codigo || null,
  motorista: getValueOrFallback(motorista?.nome),
  gestor: getValueOrFallback(motorista?.funcionario?.nome),
  cpf: getValueOrFallback(motorista?.cpf),
  cidade: getValueOrFallback(motorista?.endereco?.cidade?.descricao),
  estado: getValueOrFallback(motorista?.endereco?.cidade?.siglaUF || motorista?.estadoRG),
  disponivel: motorista?.disponivel === true,
  disponivelLabel: formatDisponibilidadeTms(motorista?.disponivel === true),
  telefone: getValueOrFallback(motorista?.endereco?.telefone || motorista?.listaContatos?.[0]?.telefone),
  email: getValueOrFallback(motorista?.email),
  categoriaCNH: getValueOrFallback(motorista?.categoriaCNH)
});

const getCargaDataAgendamento = carga => carga?.dataAgendamento || carga?.DataAgendamento || null;
const getCargaDataInicioCarregamento = carga => (
  carga?.dataInicioCarregamento
  || carga?.DataInicioCarregamento
  || carga?.inicioCarregamento
  || carga?.InicioCarregamento
  || null
);

const getUniqueTextValues = values => Array.from(new Set(
  (values || [])
    .map(value => (value === null || value === undefined ? '' : String(value).trim()))
    .filter(Boolean)
));

const getVeiculoDataAgendamentoValues = veiculo => getUniqueTextValues([
  veiculo?.dataAgendamento,
  veiculo?.DataAgendamento,
  ...(veiculo?.cargas || []).map(getCargaDataAgendamento)
]);

const getVeiculoDataInicioCarregamentoValues = veiculo => getUniqueTextValues([
  veiculo?.dataInicioCarregamento,
  veiculo?.DataInicioCarregamento,
  veiculo?.inicioCarregamento,
  veiculo?.InicioCarregamento,
  ...(veiculo?.cargas || []).map(getCargaDataInicioCarregamento)
]);

const mapViagem = (viagem, carga = null) => ({
  id: viagem?.id ?? viagem?.idViagem ?? null,
  numeroCarga: viagem?.numeroCarga || carga?.numeroCarga || '',
  numeroPedido: viagem?.numeroPedido || carga?.numeroPedidoEmbarcador || '',
  statusLogistico: getValueOrFallback(viagem?.statusLogistico?.descricao),
  origem: {
    cidadeEstado: formatCidadeEstado(
      viagem?.enderecoClienteOrigem?.cidade?.nome,
      viagem?.enderecoClienteOrigem?.cidade?.estado
    ),
    endereco: formatEnderecoLinha(viagem?.enderecoClienteOrigem),
    cliente: getValueOrFallback(
      viagem?.clienteOrigem?.nomeFantasia
      || viagem?.clienteOrigem?.razaoSocial
    )
  },
  destino: {
    cidadeEstado: formatCidadeEstado(
      viagem?.enderecoClienteDestino?.cidade?.nome,
      viagem?.enderecoClienteDestino?.cidade?.estado
    ),
    endereco: formatEnderecoLinha(viagem?.enderecoClienteDestino),
    cliente: getValueOrFallback(
      viagem?.clienteDestino?.nomeFantasia
      || viagem?.clienteDestino?.razaoSocial
    )
  },
  dataPrevisaoInicio: viagem?.dataPrevisaoInicio || null,
  dataPrevisaoFim: viagem?.dataPrevisaoFim || null,
  dataInicio: viagem?.dataInicio || null,
  dataFim: viagem?.dataFim || null,
  dataPrevisaoChegadaDestino: viagem?.dataPrevisaoChegadaDestino || null,
  previsibilidade: viagem?.previsibilidade || viagem?.Previsibilidade || null
});

const getVeiculoViagens = veiculo => {
  const rawViagens = veiculo?.viagem || veiculo?.Viagem || [];
  const viagens = Array.isArray(rawViagens) ? rawViagens : [rawViagens];

  return viagens
    .filter(Boolean)
    .map(viagem => mapViagem(viagem));
};

const normalizeNumeroCarga = value => String(value || '').trim();

const buildCargaViagemDetalhe = ({ carga, viagem }) => ({
  numeroCarga: viagem?.numeroCarga || carga?.numeroCarga || emptyValue,
  numeroPedido: viagem?.numeroPedido || carga?.numeroPedidoEmbarcador || emptyValue,
  viagemId: viagem?.id || emptyValue,
  tipoOperacao: carga?.tipoOperacao || emptyValue,
  statusViagem: viagem?.statusLogistico || emptyValue,
  dataAgendamento: carga?.dataAgendamento || emptyValue,
  dataCarregamento: carga?.dataInicioCarregamento || emptyValue,
  origem: viagem?.origem,
  destino: viagem?.destino,
  dataInicio: viagem?.dataInicio,
  dataFim: viagem?.dataFim,
  previsaoChegada: viagem?.dataPrevisaoChegadaDestino,
  kmPercurso: formatDistanceKm(viagem?.previsibilidade?.kmPercurso),
  kmRodado: formatDistanceKm(viagem?.previsibilidade?.kmRodado),
  kmRestante: formatDistanceKm(viagem?.previsibilidade?.kmRestante)
});

export const mapVeiculo = (veiculo, index = 0) => {
  const dataAgendamentoOriginalValues = getVeiculoDataAgendamentoValues(veiculo);
  const dataInicioCarregamentoOriginalValues = getVeiculoDataInicioCarregamentoValues(veiculo);
  const dataAgendamentoOriginal = dataAgendamentoOriginalValues[0] || null;
  const dataInicioCarregamentoOriginal = dataInicioCarregamentoOriginalValues[0] || null;
  const cargas = (veiculo?.cargas || []).map(carga => ({
    protocoloCarga: carga?.protocoloCarga || null,
    protocoloPedido: carga?.protocoloPedido || null,
    numero: formatCargaNumero(carga),
    numeroCarga: carga?.numeroCarga || '',
    numeroPreCarga: carga?.numeroPreCarga || '',
    numeroPedidoEmbarcador: carga?.numeroPedidoEmbarcador || '',
    dataAgendamento: getCargaDataAgendamento(carga),
    dataInicioCarregamento: getCargaDataInicioCarregamento(carga),
    tipoOperacao: getValueOrFallback(carga?.tipoOperacao?.descricao)
  }));
  const viagens = getVeiculoViagens(veiculo);
  const matchedViagemIndexes = new Set();
  const cargaViagemDetalhes = [
    ...cargas.map(carga => {
      const numeroCarga = normalizeNumeroCarga(carga?.numeroCarga);
      const viagemIndex = viagens.findIndex((viagem, index) => (
        !matchedViagemIndexes.has(index)
        && numeroCarga
        && normalizeNumeroCarga(viagem?.numeroCarga) === numeroCarga
      ));
      const viagemRelacionada = viagemIndex >= 0 ? viagens[viagemIndex] : null;

      if (viagemIndex >= 0) {
        matchedViagemIndexes.add(viagemIndex);
      }

      return buildCargaViagemDetalhe({
        carga,
        viagem: viagemRelacionada
      });
    }),
    ...viagens
      .filter((_, index) => !matchedViagemIndexes.has(index))
      .map(viagem => buildCargaViagemDetalhe({ viagem }))
  ];

  return {
    id: getVeiculoRowId(veiculo, index),
    codigo: veiculo?.codigo || null,
    protocolo: veiculo?.protocolo || null,
    tipoVeiculo: veiculo?.tipoVeiculo ?? null,
    numeroFrota: veiculo?.numeroFrota || '',
    placa: veiculo?.placa || '',
    categoria: getCategoriaVeiculo(veiculo),
    frota: getValueOrFallback(veiculo?.numeroFrota || 'Terceiro'),
    veiculo: getValueOrFallback(veiculo?.placa),
    modeloVeicular: getValueOrFallback(veiculo?.modeloVeicular?.descricao),
    dataAgendamento: formatDateTimeResumo(dataAgendamentoOriginalValues),
    dataAgendamentoOriginal,
    dataAgendamentoOriginalValues,
    dataInicioCarregamento: formatDateTimeResumo(dataInicioCarregamentoOriginalValues),
    dataInicioCarregamentoOriginal,
    dataInicioCarregamentoOriginalValues,
    tipoTracao: getValueOrFallback(veiculo?.modeloVeicular?.descricao),
    tipoReboque: getValueOrFallback(veiculo?.modeloVeicular?.descricao),
    segmentoVeiculo: getValueOrFallback(veiculo?.segmentoVeiculo?.descricao),
    tipoModelo: getTipoModeloExibicao(veiculo),
    tipoCarroceria: getValueOrFallback(veiculo?.tipoCarroceria),
    idTipoEmblema: veiculo?.idTipoEmblema ?? null,
    plotagem: getPlotagemLabel(veiculo?.idTipoEmblema),
    cidade: getValueOrFallback(veiculo?.cidade),
    estado: getValueOrFallback(veiculo?.uf),
    motorista: getValueOrFallback(veiculo?.motoristas?.[0]?.nome),
    gestor: getValueOrFallback(veiculo?.funcionario?.nome),
    disponivel: veiculo?.disponivel === true,
    disponivelLabel: formatDisponibilidadeTms(veiculo?.disponivel === true),
    mensagemDisponibilidade: getValueOrFallback(veiculo?.mensagemDisponibilidade),
    statusPatio: veiculo?.statusPatio ?? null,
    statusPatioLabel: getStatusPatioLabel(veiculo?.statusPatio),
    statusPatioVariant: getStatusPatioVariant(veiculo?.statusPatio),
    viagens,
    viagensResumo: formatViagensResumo(viagens),
    statusViagensResumo: formatStatusViagensResumo(viagens),
    pedidosViagemResumo: formatViagemValuesResumo(viagens, 'numeroPedido'),
    cargasViagemResumo: formatViagemValuesResumo(viagens, 'numeroCarga'),
    previsaoInicioViagemResumo: formatViagemDateResumo(viagens, 'dataPrevisaoInicio'),
    previsaoFimViagemResumo: formatViagemDateResumo(viagens, 'dataPrevisaoFim'),
    previsaoChegadaViagemResumo: formatViagemDateResumo(viagens, 'dataPrevisaoChegadaDestino'),
    cargaViagemDetalhes,
    cargas,
    cargasResumo: formatCargasResumo(cargas, viagens),
    tipoOperacao: formatTiposOperacaoResumo(cargas),
    motoristas: (veiculo?.motoristas || []).map(motorista => ({
      codigo: motorista?.codigo || null,
      cpf: motorista?.cpf || null,
      nome: motorista?.nome || ''
    })),
    reboques: (veiculo?.reboques || []).map(reboque => ({
      codigo: reboque?.codigo || null,
      protocolo: reboque?.protocolo || null,
      placa: reboque?.placa || ''
    })),
    capacidade: formatCapacidade(veiculo?.capacidadeKG),
    marca: getValueOrFallback(veiculo?.descricaoMarcaVeiculo),
    modelo: getValueOrFallback(veiculo?.descricaoModeloVeiculo),
    posicao: null
  };
};

export const buildResumoFrotasByStats = stats => [
  {
    id: 'cavalo',
    label: 'CAVALO',
    quantidadeDisponivel: stats.cavalo?.disponivel || 0,
    quantidadeIndisponivel: stats.cavalo?.indisponivel || 0,
    quantidadeTotal: stats.cavalo?.total || 0,
    icon: 'truck'
  },
  {
    id: 'toco',
    label: 'TOCO',
    quantidadeDisponivel: stats.toco?.disponivel || 0,
    quantidadeIndisponivel: stats.toco?.indisponivel || 0,
    quantidadeTotal: stats.toco?.total || 0,
    icon: 'truck'
  },
  {
    id: 'truck',
    label: 'TRUCK',
    quantidadeDisponivel: stats.truck?.disponivel || 0,
    quantidadeIndisponivel: stats.truck?.indisponivel || 0,
    quantidadeTotal: stats.truck?.total || 0,
    icon: 'truck'
  },
  {
    id: 'carreta',
    label: 'CARRETA',
    quantidadeDisponivel: stats.carreta?.disponivel || 0,
    quantidadeIndisponivel: stats.carreta?.indisponivel || 0,
    quantidadeTotal: stats.carreta?.total || 0,
    icon: 'truck'
  }
];

export const isDisponivelWithPatioRaw = veiculo => (
  veiculo?.disponivel === true
  && Number(veiculo?.statusPatio) === 1
);

export const isDisponivelWithPatioMapped = frota => (
  frota?.disponivel === true
  && Number(frota?.statusPatio) === 1
);

export const mapResumoFrotas = veiculos => {
  const stats = veiculos.reduce((acc, veiculo) => {
    const categoria = getCategoriaVeiculo(veiculo);
    const current = acc[categoria] || { disponivel: 0, indisponivel: 0, total: 0 };

    current.total += 1;

    if (isDisponivelWithPatioRaw(veiculo)) {
      current.disponivel += 1;
    } else {
      current.indisponivel += 1;
    }

    acc[categoria] = current;
    return acc;
  }, {});

  return buildResumoFrotasByStats(stats);
};

export const mapResumoFrotasFromMapped = frotas => {
  const stats = (frotas || []).reduce((acc, frota) => {
    const categoria = String(frota?.categoria || '').trim();

    if (!categoria) return acc;

    const current = acc[categoria] || { disponivel: 0, indisponivel: 0, total: 0 };

    current.total += 1;

    if (isDisponivelWithPatioMapped(frota)) {
      current.disponivel += 1;
    } else {
      current.indisponivel += 1;
    }

    acc[categoria] = current;
    return acc;
  }, {});

  return buildResumoFrotasByStats(stats);
};

export const isReduxValue = value => normalizeText(value).includes('redux');

export const isReduxTipoModeloVeiculo = veiculo => {
  const tipoModelo = veiculo?.tipoVeiculo === 1
    ? veiculo?.modeloVeicular?.descricao
    : (veiculo?.segmentoVeiculo?.descricao || veiculo?.modeloVeicular?.descricao);

  return isReduxValue(tipoModelo);
};

export const isReduxTipoModeloMapped = item => (
  isReduxValue(item?.tipoModelo)
  || isReduxValue(item?.tipoTracao)
  || isReduxValue(item?.tipoReboque)
);

export const sortMotoristasByDisponibilidade = data => [...(data || [])].sort((a, b) => {
  const prioridadeA = a?.disponivel ? 0 : 1;
  const prioridadeB = b?.disponivel ? 0 : 1;

  if (prioridadeA !== prioridadeB) return prioridadeA - prioridadeB;

  return String(a?.motorista || '').localeCompare(String(b?.motorista || ''), 'pt-BR');
});

export const getFrotaDisponibilidadePrioridade = item => {
  const statusDisponivel = item?.disponivel === true;
  const statusPatioDisponivel = Number(item?.statusPatio) === 1;

  if (statusDisponivel && statusPatioDisponivel) return 0;
  if (statusDisponivel) return 1;
  if (statusPatioDisponivel) return 2;

  return 3;
};

export const sortFrotasByDisponibilidade = data => [...(data || [])].sort((a, b) => {
  const prioridadeA = getFrotaDisponibilidadePrioridade(a);
  const prioridadeB = getFrotaDisponibilidadePrioridade(b);

  if (prioridadeA !== prioridadeB) return prioridadeA - prioridadeB;

  return String(a?.frota || '').localeCompare(String(b?.frota || ''), 'pt-BR');
});

export const normalizeDisponibilidadeData = data => ({
  veiculos: data?.veiculos || [],
  motoristas: data?.motoristas || []
});

export const hasNumeroFrota = veiculo => Boolean(String(veiculo?.numeroFrota || '').trim());

export const buildDadosDisponibilidade = sourceData => {
  const normalizedData = normalizeDisponibilidadeData(sourceData);
  const veiculos = normalizedData.veiculos.filter(hasNumeroFrota);
  const veiculosSemRedux = veiculos.filter(veiculo => !isReduxTipoModeloVeiculo(veiculo));
  const motoristas = normalizedData.motoristas;

  return {
    motoristas: motoristas.map(mapMotorista),
    frotasTracao: veiculosSemRedux.filter(veiculo => veiculo?.tipoVeiculo === 2).map(mapVeiculo),
    frotasReboque: veiculosSemRedux.filter(veiculo => veiculo?.tipoVeiculo === 1).map(mapVeiculo),
    resumoFrotas: mapResumoFrotas(veiculosSemRedux),
    posicoesPorChave: {}
  };
};

export const buildCategoriaDisponivelFilterOverrides = categoria => (
  categoria
    ? {
        Disponivel: {
          value: 'true',
          label: 'Disponivel'
        },
        StatusPatio: [{
          value: '1',
          label: 'Disponiveis'
        }]
      }
    : {}
);
